import { useState } from "react"
import { motion, useMotionValue, useMotionValueEvent } from "framer-motion"
import styled from "@emotion/styled"

import { useDictionary } from "contexts/Dictionary"

import getMediaQuery from "css/breakpoints"
import { a11yOutline } from "css/a11y"

import { useValue } from "./context"

const Root = styled(motion.nav)`
  position: sticky;
  top: 0;
  margin-block-start: calc(clamp(5rem, 3.4375rem + 3.9063vw, 6.875rem) * -1);

  pointer-events: none;
  height: 100vh;
  display: flex;
  align-items: center;
  z-index: 5;
`

const PageContainer = styled.ul`
  display: grid;
  grid-auto-flow: row;
  grid-gap: 0.75rem;
  top: 0;
  align-items: center;
  height: min-content;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 10px;

  &:has(.focus-visible) {
    ${a11yOutline}
    outline-offset: 0.5rem !important;
  }
  html.prefers-contrast & {
    background: #fff;
    padding: 1.25rem 0.625rem;
  }
`

const PageGroup = styled.li`
  display: flex;
`

const Label = styled(motion.a)`
  position: relative;
  width: 0.1875rem;
  border-radius: 5px;
  background: white;
  border: none;
  cursor: default;
  padding: 0;
  pointer-events: all;
  background: var(--bgcolor, #d4d4d4);

  font-size: 0;

  transition: background-color 0.3s ease-out;

  @media (hover: hover) and (pointer: fine) {
    &.unchecked:hover {
      cursor: pointer;
    }
  }

  &.unchecked::after {
    content: "";
    position: absolute;
    display: block;
    height: 1.875rem;
    width: 1.25rem;
    background: transparent;
    z-index: 0;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  ${getMediaQuery("m")} {
    &.unchecked::after {
      width: 1.875rem;
      transform: translate(-50%, -50%);
    }
  }
  html.prefers-contrast & {
    background: black;
  }
`

const variants = {
  checked: { height: "3.125rem" },
  unchecked: { height: "1.25rem" },
}

function Page({ index, label, onPageClick }) {
  const { currentIndex } = useValue()

  const [checked, setChecked] = useState(currentIndex.get() === index)

  function onSlideChange(i) {
    setChecked(index === i)
  }
  useMotionValueEvent(currentIndex, "change", onSlideChange)

  const handleClick = e => {
    e.preventDefault()
    onPageClick(index)
  }
  return (
    <PageGroup>
      <Label
        href={`#ashlar--${index}`}
        aria-current={checked}
        onClick={handleClick}
        variants={variants}
        initial={checked ? "checked" : "unchecked"}
        animate={checked ? "checked" : "unchecked"}
        className={checked ? "checked" : "unchecked"}
        transition={{ type: "tween", ease: "easeOut", duration: 0.2 }}
      >
        {label || `slide ${index + 1}`}
      </Label>
    </PageGroup>
  )
}

export default function Pagination({ pagesCount }) {
  const { targetSlide, currentIndex, isReturnVisitor } = useValue()
  const bgcolor = useMotionValue("#fff")
  const dictionary = useDictionary()

  const onPageClick = index => {
    targetSlide.set(index)
  }

  function onCurrentChange(i) {
    if (i === -1) {
      targetSlide.set(-1)
    }

    if (i === isReturnVisitor.get()) {
      bgcolor.set("#212121")
    } else {
      bgcolor.set("#fff")
    }
  }
  useMotionValueEvent(currentIndex, "change", onCurrentChange)

  return (
    <Root style={{ "--bgcolor": bgcolor }} aria-label={dictionary.navigateThePage()}>
      <PageContainer aria-label={dictionary.navigateThePage()}>
        {Array.from(Array(pagesCount)).map((a, i) => (
          <Page key={`ashlar-section-${i}`} index={i} label='' onPageClick={onPageClick} />
        ))}
      </PageContainer>
    </Root>
  )
}
